import type { AxiosError, InternalAxiosRequestConfig } from "axios";
import axios from "axios";
import API from "@/utils/api";
import { authService } from "@/services/auth.service";
import { AuthSessionError } from "@/domain/auth/auth.errors";

const REFRESH_ENDPOINT = "auth/v1/refresh";

type RetryableConfig = InternalAxiosRequestConfig & {
  _authRetry?: boolean;
};

function isRefreshRequest(config: RetryableConfig | undefined): boolean {
  const url = config?.url ?? "";
  return url.includes(REFRESH_ENDPOINT);
}

function shouldAttemptRefresh(error: AxiosError): boolean {
  const config = error.config as RetryableConfig | undefined;
  if (!config || config._authRetry) return false;
  if (isRefreshRequest(config)) return false;
  if (error.response?.status !== 401) return false;
  return Boolean(authService.getAccessToken());
}

/**
 * Registers the authentication response interceptor on the shared API client.
 * Handles single-flight token refresh and one-time request retry.
 */
export function setupAuthInterceptor(): void {
  API.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const config = error.config as RetryableConfig | undefined;

      if (!shouldAttemptRefresh(error) || !config) {
        return Promise.reject(error);
      }

      try {
        await authService.refreshTokens();
        await authService.loadProfile({ preserveEnrichment: true });
      } catch (refreshError) {
        if (
          refreshError instanceof AuthSessionError ||
          axios.isAxiosError(refreshError)
        ) {
          await authService.invalidateSession();
        }
        return Promise.reject(error);
      }

      config._authRetry = true;
      return API.request(config);
    }
  );
}
